import React from 'react';
import {
  Box,
  Chip,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Typography,
  Divider,
  Alert,
  Button,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';

const formatBytes = (bytes = 0) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
};

const getStatusProps = (status) => {
  switch (status) {
    case 'success': return { label: 'Skontrolované', color: 'success' };
    case 'queued': return { label: 'V rade (PDF)', color: 'secondary' };
    case 'error': return { label: 'Chyba', color: 'error' };
    case 'uploading': return { label: 'Prebieha...', color: 'info' };
    default: return { label: 'Čaká na kontrolu', color: 'default' };
  }
};

// Chip pre jednu skupinu: zelený keď 0 (prešlo), červený keď > 0 (neprešlo)
const GroupChip = ({ label, count }) => (
  <Chip
    size="small"
    label={`${label}: ${count}`}
    color={count > 0 ? 'error' : 'success'}
    variant={count > 0 ? 'filled' : 'outlined'}
    sx={{ borderRadius: 1.5, fontWeight: 600 }}
  />
);

const PIIValidationFileList = ({ files, isUploading, onRemove, onViewReport }) => {
  if (!files?.length) return null;

  return (
    <Stack spacing={1.5} sx={{ mb: 3 }}>
      {files.map((f) => {
        const statusProps = getStatusProps(f.status);
        const report = f.report || null;

        const gdprCount = Number(report?.gdpr_values_count || 0);
        const nis2Count = Number(report?.nis2_values_count ?? report?.nis2?.total ?? 0);
        const vyhCount = Number(report?.vyhradene_values_count ?? report?.vyhradene?.total ?? 0);
        const totalFailed = gdprCount + nis2Count + vyhCount;
        const shouldNotShare = !!report?.should_not_share || totalFailed > 0;

        return (
          <Paper
            key={f.id}
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" noWrap title={f.name} sx={{ fontWeight: 600 }}>
                  {f.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatBytes(f.size)}
                  {report?.pages > 0 && ` • ${report.pages} strán`}
                </Typography>
              </Box>

              <Chip
                size="small"
                color={statusProps.color}
                label={statusProps.label}
                sx={{ borderRadius: 1.5 }}
              />

              <IconButton size="small" disabled={isUploading} onClick={() => onRemove(f.id)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>

            {/* Progress Bar */}
            {f.status === 'uploading' && (
              <Box sx={{ mt: 1.5 }}>
                <LinearProgress
                  variant="determinate"
                  value={f.progress}
                  sx={{ borderRadius: 2, height: 6 }}
                />
              </Box>
            )}

            {/* Error Message */}
            {f.status === 'error' && f.error && (
              <Typography variant="body2" color="error" sx={{ mt: 1, fontWeight: 600 }}>
                {f.error}
              </Typography>
            )}

            {/* Success Results */}
            {f.status === 'success' && report && (
              <Box
                sx={{
                  mt: 2,
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: 'action.hover',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  spacing={1.5}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      color={shouldNotShare ? 'error.main' : 'success.main'}
                      fontWeight={600}
                      sx={{ mb: 0.75 }}
                    >
                      {shouldNotShare
                        ? `Neprešlo ${totalFailed} ukazovateľov v 3 skupinách`
                        : 'Všetky skupiny prešli'}
                    </Typography>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                      <GroupChip label="GDPR" count={gdprCount} />
                      <GroupChip label="NIS2" count={nis2Count} />
                      <GroupChip label="Vyhradené" count={vyhCount} />
                    </Stack>
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    color="inherit"
                    startIcon={<VisibilityIcon />}
                    onClick={() => onViewReport(f)}
                    sx={{ borderRadius: 2, fontWeight: 600, flexShrink: 0 }}
                  >
                    Detail a anonymizácia
                  </Button>
                </Stack>
              </Box>
            )}

            {/* Queued Message */}
            {f.status === 'queued' && (
              <Box sx={{ mt: 2 }}>
                <Divider sx={{ mb: 1.5 }} />
                <Alert severity="info" sx={{ borderRadius: 2, '& .MuiAlert-message': { py: 0.5 } }}>
                  Tento PDF dokument bol odoslaný na spracovanie na pozadí. Výsledok sa čoskoro zobrazí v histórii vedľa.
                </Alert>
              </Box>
            )}
          </Paper>
        );
      })}
    </Stack>
  );
};

export default PIIValidationFileList;
